/**
 * Custom fetcher for Orval-generated API client.
 * Wraps the existing apiClient to maintain:
 * - Token management (auto-attach, refresh)
 * - Error handling
 * - Base URL resolution
 */
import { api, ApiResponse } from './apiClient';
import type { AxiosRequestConfig } from 'axios';

/**
 * Custom instance for Orval's generated client.
 * Orval generates functions that call this with the request config.
 * 
 * @param config - Axios request configuration from Orval
 * @returns The response data (unwrapped from ApiResponse)
 */
export const customInstance = async <T>(
  config: AxiosRequestConfig
): Promise<T> => {
  const { url, method = 'GET', data, params, headers, ...rest } = config;

  try {
    let response;

    switch (method.toUpperCase()) {
      case 'GET':
        response = await api.get<T>(url!, { params, headers, ...rest });
        break;
      case 'POST':
        response = await api.post<T>(url!, data, { params, headers, ...rest });
        break;
      case 'PUT':
        response = await api.put<T>(url!, data, { params, headers, ...rest });
        break;
      case 'PATCH':
        response = await api.patch<T>(url!, data, { params, headers, ...rest });
        break;
      case 'DELETE':
        response = await api.delete<T>(url!, { params, headers, ...rest });
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    // apiClient returns AxiosResponse<ApiResponse<T>>
    // We need to extract the data from the ApiResponse wrapper
    const apiResponse = response.data as ApiResponse<T>;
    
    if (apiResponse.data !== undefined) {
      return apiResponse.data;
    }
    
    // For void responses or responses without data property
    return apiResponse as unknown as T;
  } catch (error) {
    // Re-throw the error as-is since apiClient already handles formatting
    throw error;
  }
};

/**
 * Type for form data uploads.
 * Use this when the endpoint expects multipart/form-data.
 */
export const customFormDataInstance = async <T>(
  config: AxiosRequestConfig
): Promise<T> => {
  const { url, method = 'POST', data, headers, ...rest } = config;

  const response = await api.post<T>(url!, data, {
    headers: {
      ...headers,
      'Content-Type': 'multipart/form-data',
    },
    ...rest,
  });

  const apiResponse = response.data as ApiResponse<T>;
  
  if (apiResponse.data !== undefined) {
    return apiResponse.data;
  }
  
  return apiResponse as unknown as T;
};

export default customInstance;
